import { Button } from '@gitbook/spine-react';
import { assertNever } from '@gitbook/util-ts';

import { ContentIcon } from '../../../Components';
import { prompt } from './prompts';

export type AIProvider = 'claude' | 'cursor' | 'codex';

/**
 * Open AI provider app with the onboarding prompt.
 *
 * If a desktop app is available, the deep link will prompt the user to open the app,
 * otherwise it will open the web version of the app.
 */
export function AIProviderLaunchButton(props: {
    provider: AIProvider;
    onLaunch?: (provider: AIProvider) => void;
}) {
    const { provider, onLaunch } = props;
    const providerProps = getAIProviderProps(provider);
    const { label, icon, urls } = providerProps;

    return (
        <Button
            kind="secondary"
            size="medium"
            label={label}
            tooltip={{ label: `Open prompt in ${label}` }}
            leadingIcon={icon}
            onPress={() => {
                openAIProviderURLs(urls);
                onLaunch?.(provider);
            }}
            className="flex-1"
        />
    );
}
/**
 * Open the AI provider app with the onboarding prompt.
 *
 * We first attempt to open the provider's desktop app and fallback to opening the web app.
 * Browsers don't have a way to detect if a deep link opened successfully. To emulate this,
 * we watch for clues that the user left the page (eg: blur, pagehide, visibilitychange).
 */
function openAIProviderURLs(urls: { desktop?: string; web: string }) {
    const { desktop, web } = urls;

    if (!desktop) {
        window.open(web, '_blank', 'noopener,noreferrer');
        return;
    }

    const fallbackWindow = window.open('', '_blank');
    let hasLeftPage = false;
    const markAsOpened = () => {
        hasLeftPage = true;
    };
    const markAsOpenedWhenHidden = () => {
        if (document.hidden) {
            markAsOpened();
        }
    };

    window.addEventListener('blur', markAsOpened, { once: true });
    window.addEventListener('pagehide', markAsOpened, { once: true });
    document.addEventListener('visibilitychange', markAsOpenedWhenHidden, { once: true });

    window.location.assign(desktop);

    window.setTimeout(() => {
        window.removeEventListener('blur', markAsOpened);
        window.removeEventListener('pagehide', markAsOpened);
        document.removeEventListener('visibilitychange', markAsOpenedWhenHidden);

        if (hasLeftPage) {
            fallbackWindow?.close();
            return;
        }

        if (fallbackWindow) {
            fallbackWindow.opener = null;
            fallbackWindow.location.replace(web);
        } else {
            window.location.assign(web);
        }
    }, 300);
}

function getAIProviderProps(provider: AIProvider) {
    switch (provider) {
        case 'claude':
            return {
                label: 'Claude Code',
                icon: <ContentIcon icon="claude" />,
                urls: getAIProviderPromptURLs('claude'),
            };
        case 'cursor':
            return {
                label: 'Cursor',
                icon: <ContentIcon icon="cursor" />,
                urls: getAIProviderPromptURLs('cursor'),
            };
        case 'codex':
            return {
                label: 'Codex',
                icon: <ContentIcon icon="openai" />,
                urls: getAIProviderPromptURLs('codex'),
            };
        default:
            assertNever(provider);
    }
}

function getAIProviderPromptURLs(provider: AIProvider) {
    let webURL: URL;
    let desktopURL: URL | undefined;

    switch (provider) {
        case 'claude':
            webURL = new URL(CLAUDE_PROMPT_URL);
            webURL.searchParams.set('q', prompt.claude);
            desktopURL = new URL(CLAUDE_DESKTOP_PROMPT_URL);
            desktopURL.searchParams.set('q', prompt.claude);
            break;
        case 'cursor':
            webURL = new URL(CURSOR_PROMPT_URL);
            webURL.searchParams.set('text', prompt.cursor);
            desktopURL = new URL(CURSOR_DESKTOP_PROMPT_URL);
            desktopURL.searchParams.set('text', prompt.cursor);
            break;
        case 'codex':
            webURL = new URL(CODEX_PROMPT_URL);
            webURL.searchParams.set('prompt', prompt.codex);
            break;
        default:
            assertNever(provider);
    }

    return {
        desktop: desktopURL?.toString(),
        web: webURL.toString(),
    };
}
const CLAUDE_PROMPT_URL = 'https://claude.ai/new';
const CLAUDE_DESKTOP_PROMPT_URL = 'claude://code/new';
const CURSOR_PROMPT_URL = 'https://cursor.com/link/prompt';
const CURSOR_DESKTOP_PROMPT_URL = 'cursor://anysphere.cursor-deeplink/prompt';
const CODEX_PROMPT_URL = 'https://chatgpt.com/codex/deeplink';
